import { parseTimezoneOffsetMinutes } from './dashaCalculator';

// Timing convention: mean sidereal year, not JHora's true solar-return years.
const YEAR_MS = 365.256364 * 24 * 60 * 60 * 1000;

const MOVABLE = [0, 3, 6, 9];
const FIXED = [1, 4, 7, 10];
const DUAL = [2, 5, 8, 11];

const ARGALA_PLANETS = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];
const ARGALA_BENEFICS = ['JUPITER', 'VENUS', 'MERCURY', 'MOON'];
const ARGALA_MALEFICS = ['SUN', 'MARS', 'SATURN', 'RAHU', 'KETU'];
const KARAKA_NAMES_7 = ['AK', 'AmK', 'BK', 'MK', 'PK', 'GK', 'DK'];
const KARAKA_NAMES_8 = ['AK', 'AmK', 'BK', 'MK', 'PiK', 'PK', 'GK', 'DK'];
const KARAKA_LABELS: Record<string, string> = {
  AK: 'Atma Karaka',
  AmK: 'Amatya Karaka',
  BK: 'Bhratru Karaka',
  MK: 'Matru Karaka',
  PiK: 'Pitru Karaka',
  PK: 'Putra Karaka',
  GK: 'Gnati Karaka',
  DK: 'Dara Karaka'
};

const SIGN_LORD_KEYS: Record<number, string> = {
  0: 'MARS',
  1: 'VENUS',
  2: 'MERCURY',
  3: 'MOON',
  4: 'SUN',
  5: 'MERCURY',
  6: 'VENUS',
  7: 'MARS',
  8: 'JUPITER',
  9: 'SATURN',
  10: 'SATURN',
  11: 'JUPITER'
};

const DUAL_LORD_KEYS: Record<number, [string, string]> = {
  7: ['MARS', 'KETU'],
  10: ['SATURN', 'RAHU']
};

export const SIGN_NAMES = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces'
];

export interface ArudhaProjection {
  AL: number;
  houses: Record<number, number>;
  padas: Record<string, number>;
  longitudes?: Record<number, number>;
  chartByPlanet: Record<string, { sign: number; signName: string; houseFromAL: number }>;
  planetsByHouseFromAL: Record<number, string[]>;
}

export interface JaiminiSnapshot {
  lagnaSign: number;
  lagnaName: string;
  karakamshaSign: number | null;
  karakamshaName: string | null;
  upapadaLagna: number | null;
  upapadaLagnaName: string | null;
  charaKarakas: Array<{ karaka: string; label: string; planet: string; degreeInSign: number }>;
  currentCharaDasha: {
    sign: number;
    signName: string;
    years: number;
    start: Date;
    end: Date;
  } | null;
  currentCharaAntardasha: {
    sign: number;
    signName: string;
    years: number;
    start: Date;
    end: Date;
  } | null;
  currentCharaPratyantardasha: {
    sign: number;
    signName: string;
    years: number;
    start: Date;
    end: Date;
  } | null;
  charaDasha: Array<{
    sign: number;
    signName: string;
    years: number;
    start: Date;
    end: Date;
    antardasha: Array<{
      sign: number;
      signName: string;
      years: number;
      start: Date;
      end: Date;
      pratyantardasha: Array<{
        sign: number;
        signName: string;
        years: number;
        start: Date;
        end: Date;
      }>;
    }>;
  }>;
  arudhaPadas: {
    AL: number;
    houses: Record<number, number>;
  };
  arudha: ArudhaProjection;
  lagnaDrishti: number[];
  navamsaByPlanet: Record<string, { sign: number; signName: string }>;
  argalaBySign: Record<number, {
    second: ArgalaResult;
    fourth: ArgalaResult;
    fifth: ArgalaResult;
    eleventh: ArgalaResult;
    specialThirdPapaArgala: SpecialThirdPapaArgala;
  }>;
}

interface ArgalaNature {
  benefics: string[];
  malefics: string[];
}

interface ArgalaResult {
  key: 'second' | 'fourth' | 'fifth' | 'eleventh';
  label: string;
  argalaSign: string;
  obstructionSign: string;
  argalaPlanets: string[];
  obstructionPlanets: string[];
  strength: 'strong' | 'partial' | 'neutral' | 'obstructed';
  status: 'strong' | 'partial' | 'neutral' | 'obstructed';
  reason: string;
  type: 'none' | 'subha' | 'papa' | 'mixed';
  argalaNature: ArgalaNature;
  obstructionNature: ArgalaNature;
}

interface SpecialThirdPapaArgala {
  active: boolean;
  label?: string;
  status?: 'strong';
  reason?: string;
  sign?: string;
  planets?: string[];
}

export interface JaiminiThemePrediction {
  score: number;
  prediction: string;
  details: string[];
  activePeriods: Array<{
    sign: number;
    signName: string;
    start: Date;
    end: Date;
  }>;
}

export interface KarakaScoreResult {
  karaka: string;
  planet: string;
  score: number;
  interpretation: string;
  details: string[];
}

export interface JaiminiPredictionReport {
  karakas: Record<string, KarakaScoreResult>;
  wealth: JaiminiThemePrediction;
  marriage: JaiminiThemePrediction;
}

const normalizeLongitude = (longitude: number) => ((longitude % 360) + 360) % 360;

const isAdjacent = (a: number, b: number) => {
  const diff = Math.abs(a - b);
  return diff === 1 || diff === 11;
};

// Jaimini's odd-footed/even-footed grouping is not zodiacal parity.
// Odd-footed: Aries, Taurus, Gemini, Libra, Scorpio, Sagittarius.
const ODD_FOOTED_SIGNS = new Set([0, 1, 2, 6, 7, 8]);
const isOddFootedSign = (sign: number) => ODD_FOOTED_SIGNS.has(sign);

// K.N. Rao, Chara Dasha Order: sequence direction comes from the ninth sign.
const getDirection = (sign: number) => (isOddFootedSign((sign + 8) % 12) ? 1 : -1);

const getSignSequence = (startSign: number, direction: number) => {
  const normalizedDirection = direction >= 0 ? 1 : -1;
  const sequence: number[] = [];

  for (let i = 0; i < 12; i += 1) {
    sequence.push((startSign + (i * normalizedDirection) + 120) % 12);
  }

  return sequence;
};

// K.N. Rao sub-periods start from the adjacent sign and end with the parent sign.
const getSubPeriodSequence = (parentSign: number) => {
  const direction = getDirection(parentSign);
  return getSignSequence((parentSign + direction + 12) % 12, direction);
};

const countForward = (start: number, end: number) => {
  let count = 1;
  let current = start;

  while (current !== end) {
    current = (current + 1) % 12;
    count += 1;
  }

  return count;
};

const countBackward = (start: number, end: number) => {
  let count = 1;
  let current = start;

  while (current !== end) {
    current = (current - 1 + 12) % 12;
    count += 1;
  }

  return count;
};

const getRashiDrishti = (sign: number) => {
  if (MOVABLE.includes(sign)) {
    return FIXED.filter((s) => !isAdjacent(sign, s));
  }

  if (FIXED.includes(sign)) {
    return MOVABLE.filter((s) => !isAdjacent(sign, s));
  }

  if (DUAL.includes(sign)) {
    return DUAL.filter((s) => s !== sign);
  }

  return [];
};

const getNavamsaSign = (longitude: number) => {
  const normalized = normalizeLongitude(longitude);
  const sign = Math.floor(normalized / 30);
  const deg = normalized % 30;
  const navamsa = Math.floor(deg / (30 / 9));
  return (sign * 9 + navamsa) % 12;
};

const getArudhaPada = (houseSign: number, lordSign: number) => {
  const distance = (lordSign - houseSign + 12) % 12;
  let pada = (lordSign + distance) % 12;

  if (pada === houseSign || pada === (houseSign + 6) % 12) {
    pada = (pada + 9) % 12;
  }

  return pada;
};

const getArudhaPadaLongitude = (houseLongitude: number, lordLongitude: number) => {
  const normalizedHouse = normalizeLongitude(houseLongitude);
  const houseSign = Math.floor(normalizedHouse / 30);
  const normalizedLord = normalizeLongitude(lordLongitude);
  const lordSign = Math.floor(normalizedLord / 30);
  const reflectedLongitude = normalizeLongitude(lordLongitude + normalizeLongitude(lordLongitude - normalizedHouse));
  const padaSign = getArudhaPada(houseSign, lordSign);

  return normalizeLongitude((padaSign * 30) + (reflectedLongitude % 30));
};

const getBirthInstant = (chartData: any): Date | null => {
  const birthDateValue = chartData?.birthDetails?.dateOfBirth;
  const birthDate = new Date(birthDateValue);

  if (!birthDateValue || Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const timeOfBirth = chartData?.birthDetails?.timeOfBirth || {};
  const timezoneMinutes = parseTimezoneOffsetMinutes(chartData?.birthDetails?.placeOfBirth?.timezone);

  const utcMillis = Date.UTC(
    birthDate.getUTCFullYear(),
    birthDate.getUTCMonth(),
    birthDate.getUTCDate(),
    Number(timeOfBirth.hour) || 0,
    Number(timeOfBirth.minute) || 0,
    Number(timeOfBirth.second) || 0
  ) - (timezoneMinutes * 60 * 1000);

  return new Date(utcMillis);
};

const toSignIndex = (value: any): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;

  if (value >= 1 && value <= 12) return value - 1;
  if (value >= 0 && value < 12) return Math.floor(value);

  return null;
};

const buildPlanetSignMap = (chartData: any): Record<string, number> => {
  const result: Record<string, number> = {};

  Object.entries(chartData?.planets || {}).forEach(([key, value]) => {
    const planet = value as any;
    const signIndex = toSignIndex(planet?.sign?.number)
      ?? (typeof planet?.longitude === 'number' ? Math.floor(normalizeLongitude(planet.longitude) / 30) : null);

    if (signIndex !== null) {
      result[String(key).toUpperCase()] = signIndex;
    }
  });

  return result;
};

const buildPlanetLongitudeMap = (chartData: any): Record<string, number> => {
  const result: Record<string, number> = {};

  Object.entries(chartData?.planets || {}).forEach(([key, value]) => {
    const longitude = (value as any)?.longitude;
    if (typeof longitude === 'number' && Number.isFinite(longitude)) {
      result[String(key).toUpperCase()] = normalizeLongitude(longitude);
    }
  });

  return result;
};

const countAssociations = (planetKey: string, planetSignMap: Record<string, number>) => {
  const sign = planetSignMap[planetKey];
  if (typeof sign !== 'number') return -1;
  return Object.values(planetSignMap).filter((planetSign) => planetSign === sign).length;
};

const countRashiAspectsToSign = (targetSign: number, planetSignMap: Record<string, number>) => (
  Object.values(planetSignMap).filter((planetSign) => (
    typeof planetSign === 'number' && getRashiDrishti(planetSign).includes(targetSign)
  )).length
);

const placementStrengthFromSign = (baseSign: number, planetSign: number) => {
  const offset = (planetSign - baseSign + 12) % 12;
  if ([0, 3, 6, 9].includes(offset)) return 3;
  if ([1, 4, 7, 10].includes(offset)) return 2;
  return 1;
};

const getStrongestLordKey = (
  sign: number,
  planetSignMap: Record<string, number>,
  charaKarakas: Array<{ karaka: string; planet: string }> = []
) => {
  const dualLords = DUAL_LORD_KEYS[sign];
  if (!dualLords) return SIGN_LORD_KEYS[sign];

  const [classicalLord, nodeLord] = dualLords;
  const classicalSign = planetSignMap[classicalLord];
  const nodeSign = planetSignMap[nodeLord];
  if (typeof classicalSign !== 'number') return typeof nodeSign === 'number' ? nodeLord : classicalLord;
  if (typeof nodeSign !== 'number') return classicalLord;

  const atmakaraka = charaKarakas.find((item) => item.karaka === 'AK')?.planet;
  if (atmakaraka === classicalLord) return classicalLord;
  if (classicalSign === sign && nodeSign !== sign) return nodeLord;
  if (nodeSign === sign && classicalSign !== sign) return classicalLord;
  if (classicalSign === nodeSign) return classicalLord;

  const classicalPlacement = placementStrengthFromSign(sign, classicalSign);
  const nodePlacement = placementStrengthFromSign(sign, nodeSign);
  if (classicalPlacement !== nodePlacement) return classicalPlacement > nodePlacement ? classicalLord : nodeLord;

  const classicalAssociations = countAssociations(classicalLord, planetSignMap);
  const nodeAssociations = countAssociations(nodeLord, planetSignMap);
  if (classicalAssociations !== nodeAssociations) {
    return classicalAssociations > nodeAssociations ? classicalLord : nodeLord;
  }

  const classicalAspects = countRashiAspectsToSign(classicalSign, planetSignMap);
  const nodeAspects = countRashiAspectsToSign(nodeSign, planetSignMap);
  if (classicalAspects !== nodeAspects) return classicalAspects > nodeAspects ? classicalLord : nodeLord;

  return classicalLord;
};

const getLordSign = (
  sign: number,
  planetSignMap: Record<string, number>,
  charaKarakas: Array<{ karaka: string; planet: string }> = []
): number | null => {
  const lord = getStrongestLordKey(sign, planetSignMap, charaKarakas);
  const lordSign = planetSignMap[lord];
  return typeof lordSign === 'number' ? lordSign : null;
};

// Dasha co-lord strength has its own rules; do not reuse Arudha strength.
const getCharaDashaLordSign = (
  sign: number,
  planetSignMap: Record<string, number>,
  planetLongitudeMap: Record<string, number>,
): number | null => {
  const dualLords = DUAL_LORD_KEYS[sign];
  if (!dualLords) return planetSignMap[SIGN_LORD_KEYS[sign]] ?? null;

  const [classicalLord, nodeLord] = dualLords;
  const classicalSign = planetSignMap[classicalLord];
  const nodeSign = planetSignMap[nodeLord];
  if (typeof classicalSign !== 'number') return nodeSign ?? null;
  if (typeof nodeSign !== 'number') return classicalSign;
  if (classicalSign === sign && nodeSign === sign) return sign;
  if (classicalSign === sign) return nodeSign;
  if (nodeSign === sign) return classicalSign;

  const companions = (lord: string) => ARGALA_PLANETS.filter((planet) => (
    planet !== lord && planetSignMap[planet] === planetSignMap[lord]
  )).length;
  const classicalCompanions = companions(classicalLord);
  const nodeCompanions = companions(nodeLord);
  if (classicalCompanions !== nodeCompanions) {
    return classicalCompanions > nodeCompanions ? classicalSign : nodeSign;
  }

  // Use actual degrees, including seconds; Rahu reversal applies only to Karakas.
  const classicalDegree = (planetLongitudeMap[classicalLord] ?? 0) % 30;
  const nodeDegree = (planetLongitudeMap[nodeLord] ?? 0) % 30;
  return nodeDegree > classicalDegree ? nodeSign : classicalSign;
};

const getDashaLength = (sign: number, planetSignMap: Record<string, number>, planetLongitudeMap: Record<string, number>) => {
  const lordSign = getCharaDashaLordSign(sign, planetSignMap, planetLongitudeMap);
  if (lordSign === null) return 1;
  const inclusiveCount = isOddFootedSign(sign)
    ? countForward(sign, lordSign)
    : countBackward(sign, lordSign);

  return inclusiveCount === 1 ? 12 : inclusiveCount - 1;
};

const getPlanetsBySign = (planetSignMap: Record<string, number>) => {
  const map: Record<number, string[]> = {
    0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [], 10: [], 11: []
  };

  for (const planet of ARGALA_PLANETS) {
    const sign = planetSignMap[planet];
    if (typeof sign === 'number') {
      map[sign].push(planet);
    }
  }

  return map;
};

const getMoonWaxing = (chartData: any) => {
  const moonLon = chartData?.planets?.MOON?.longitude;
  const sunLon = chartData?.planets?.SUN?.longitude;

  if (typeof moonLon !== 'number' || typeof sunLon !== 'number') {
    return true;
  }

  const separation = (normalizeLongitude(moonLon) - normalizeLongitude(sunLon) + 360) % 360;
  return separation < 180;
};

const isBeneficPlanet = (planet: string, moonWaxing: boolean) => {
  if (planet === 'JUPITER' || planet === 'VENUS' || planet === 'MERCURY') return true;
  if (planet === 'MOON') return moonWaxing;
  return false;
};

const classifyPlanets = (planets: string[]): ArgalaNature => {
  const benefics: string[] = [];
  const malefics: string[] = [];

  planets.forEach((planet) => {
    if (ARGALA_BENEFICS.includes(planet)) {
      benefics.push(planet);
      return;
    }
    if (ARGALA_MALEFICS.includes(planet)) malefics.push(planet);
  });

  return { benefics, malefics };
};

const getArgalaType = (planets: string[]): ArgalaResult['type'] => {
  if (!planets.length) return 'none';
  const nature = classifyPlanets(planets);
  if (nature.benefics.length > nature.malefics.length) return 'subha';
  if (nature.malefics.length > nature.benefics.length) return 'papa';
  return 'mixed';
};

const analyzeArgala = (
  key: ArgalaResult['key'],
  label: string,
  argalaSignIndex: number,
  obstructionSignIndex: number,
  argalaPlanets: string[],
  obstructionPlanets: string[]
): ArgalaResult => {
  let status: ArgalaResult['status'] = 'neutral';
  let reason = 'No Argala planets';

  if (argalaPlanets.length && !obstructionPlanets.length) {
    status = 'strong';
    reason = 'No obstruction';
  } else if (argalaPlanets.length && obstructionPlanets.length >= argalaPlanets.length) {
    status = 'obstructed';
    reason = 'Obstruction planets are equal or stronger';
  } else if (argalaPlanets.length) {
    status = 'partial';
    reason = 'Argala exists but partially obstructed';
  }

  return {
    key,
    label,
    argalaSign: SIGN_NAMES[argalaSignIndex],
    obstructionSign: SIGN_NAMES[obstructionSignIndex],
    argalaPlanets,
    obstructionPlanets,
    strength: status,
    status,
    reason,
    type: getArgalaType(argalaPlanets),
    argalaNature: classifyPlanets(argalaPlanets),
    obstructionNature: classifyPlanets(obstructionPlanets)
  };
};

const houseFrom = (base: number, offset: number) => (base + offset) % 12;

const evaluateArgalaForSign = (sign: number, planetsBySign: Record<number, string[]>, _moonWaxing: boolean) => {
  const second = houseFrom(sign, 1);
  const fourth = houseFrom(sign, 3);
  const fifth = houseFrom(sign, 4);
  const eleventh = houseFrom(sign, 10);

  const twelfth = houseFrom(sign, 11);
  const tenth = houseFrom(sign, 9);
  const ninth = houseFrom(sign, 8);
  const third = houseFrom(sign, 2);
  const thirdHouseMalefics = planetsBySign[third].filter((planet) => ARGALA_MALEFICS.includes(planet));

  return {
    second: analyzeArgala('second', '2nd Argala', second, twelfth, planetsBySign[second], planetsBySign[twelfth]),
    fourth: analyzeArgala('fourth', '4th Argala', fourth, tenth, planetsBySign[fourth], planetsBySign[tenth]),
    fifth: analyzeArgala('fifth', '5th Argala', fifth, ninth, planetsBySign[fifth], planetsBySign[ninth]),
    eleventh: analyzeArgala('eleventh', '11th Argala', eleventh, third, planetsBySign[eleventh], planetsBySign[third]),
    specialThirdPapaArgala: thirdHouseMalefics.length >= 3
      ? {
          active: true,
          label: 'Special 3rd Papa Argala',
          status: 'strong' as const,
          reason: '3rd house has 3 or more malefics. This Argala is not cancelled.',
          sign: SIGN_NAMES[third],
          planets: thirdHouseMalefics
        }
      : { active: false }
  };
};

const calculateCharaKarakas = (chartData: any, useRahu: boolean = true) => {
  const karakaNames = useRahu ? KARAKA_NAMES_8 : KARAKA_NAMES_7;
  const keys = useRahu
    ? ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU']
    : ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN'];

  const ranked = keys
    .map((planetKey) => {
      const longitude = chartData?.planets?.[planetKey]?.longitude;
      if (typeof longitude !== 'number') return null;

      const lon = normalizeLongitude(longitude);
      const rawDegreeInSign = lon % 30;
      const degreeInSign = planetKey === 'RAHU' ? 30 - rawDegreeInSign : rawDegreeInSign;

      return { planetKey, degreeInSign };
    })
    .filter(Boolean) as Array<{ planetKey: string; degreeInSign: number }>;

  ranked.sort((a, b) => b.degreeInSign - a.degreeInSign);

  return ranked.slice(0, karakaNames.length).map((entry, index) => {
    const karaka = karakaNames[index];
    return {
      karaka,
      label: KARAKA_LABELS[karaka],
      planet: entry.planetKey,
      degreeInSign: entry.degreeInSign
    };
  });
};

export const calculateArudhaFromSignMap = (
  lagnaSign: number,
  planetSignMap: Record<string, number>,
  charaKarakas: Array<{ karaka: string; planet: string }> = []
): ArudhaProjection => {
  const houses: Record<number, number> = {};
  const padas: Record<string, number> = {};

  for (let house = 1; house <= 12; house += 1) {
    const houseSign = (lagnaSign + (house - 1)) % 12;
    const lordSign = getLordSign(houseSign, planetSignMap, charaKarakas) ?? houseSign;
    houses[house] = getArudhaPada(houseSign, lordSign);
    padas[`A${house}`] = houses[house];
  }

  const AL = houses[1];

  const chartByPlanet: Record<string, { sign: number; signName: string; houseFromAL: number }> = {};
  const planetsByHouseFromAL: Record<number, string[]> = {
    1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [], 10: [], 11: [], 12: []
  };

  Object.entries(planetSignMap).forEach(([planetKey, sign]) => {
    if (typeof sign !== 'number') return;

    const houseFromAL = ((sign - AL + 12) % 12) + 1;
    const upperPlanet = String(planetKey).toUpperCase();

    chartByPlanet[upperPlanet] = {
      sign,
      signName: SIGN_NAMES[sign],
      houseFromAL
    };

    planetsByHouseFromAL[houseFromAL].push(upperPlanet);
  });

  return {
    AL,
    houses,
    padas,
    chartByPlanet,
    planetsByHouseFromAL
  };
};

const calculateArudhaFromChartData = (
  chartData: any,
  lagnaSign: number,
  planetSignMap: Record<string, number>,
  charaKarakas: Array<{ karaka: string; planet: string }> = []
): ArudhaProjection => {
  const planetLongitudeMap = buildPlanetLongitudeMap(chartData);
  const ascendantLongitude = chartData?.houses?.ascendant?.longitude;

  if (typeof ascendantLongitude !== 'number' || Object.keys(planetLongitudeMap).length === 0) {
    return calculateArudhaFromSignMap(lagnaSign, planetSignMap, charaKarakas);
  }

  const houses: Record<number, number> = {};
  const padas: Record<string, number> = {};
  const longitudes: Record<number, number> = {};

  for (let house = 1; house <= 12; house += 1) {
    const housePoint = normalizeLongitude(ascendantLongitude + ((house - 1) * 30));
    const houseSign = Math.floor(normalizeLongitude(housePoint) / 30);
    const lordKey = getStrongestLordKey(houseSign, planetSignMap, charaKarakas);
    const lordLongitude = planetLongitudeMap[lordKey];

    if (typeof lordLongitude === 'number') {
      const padaLongitude = getArudhaPadaLongitude(housePoint, lordLongitude);
      longitudes[house] = padaLongitude;
      houses[house] = Math.floor(padaLongitude / 30);
    } else {
      const lordSign = getLordSign(houseSign, planetSignMap, charaKarakas) ?? houseSign;
      houses[house] = getArudhaPada(houseSign, lordSign);
    }

    padas[`A${house}`] = houses[house];
  }

  const AL = houses[1];
  const chartByPlanet: Record<string, { sign: number; signName: string; houseFromAL: number }> = {};
  const planetsByHouseFromAL: Record<number, string[]> = {
    1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [], 10: [], 11: [], 12: []
  };

  Object.entries(planetSignMap).forEach(([planetKey, sign]) => {
    if (typeof sign !== 'number') return;
    const houseFromAL = ((sign - AL + 12) % 12) + 1;
    const upperPlanet = String(planetKey).toUpperCase();
    chartByPlanet[upperPlanet] = { sign, signName: SIGN_NAMES[sign], houseFromAL };
    planetsByHouseFromAL[houseFromAL].push(upperPlanet);
  });

  return { AL, houses, padas, longitudes, chartByPlanet, planetsByHouseFromAL };
};

const interpretScore = (score: number) => {
  if (score >= 5) return 'very strong positive results';
  if (score >= 2) return 'good results';
  if (score >= 0) return 'mixed results';
  return 'challenging results';
};

const interpretWealth = (score: number) => {
  if (score >= 5) return 'high wealth potential';
  if (score >= 2) return 'moderate wealth';
  return 'financial fluctuations';
};

const interpretMarriage = (score: number) => {
  if (score >= 6) return 'strong marriage potential';
  if (score >= 3) return 'supportive marriage tendencies';
  if (score >= 0) return 'mixed marriage outcomes';
  return 'challenging relationship phase';
};

export const calculateJaiminiSnapshot = (chartData: any, asOfDate: Date = new Date(), useRahu: boolean = true): JaiminiSnapshot | null => {
  const lagnaSign = toSignIndex(chartData?.houses?.ascendant?.sign?.number)
    ?? (typeof chartData?.houses?.ascendant?.longitude === 'number'
      ? Math.floor(normalizeLongitude(chartData.houses.ascendant.longitude) / 30)
      : null);

  const birthInstant = getBirthInstant(chartData);

  if (lagnaSign === null || !birthInstant) {
    return null;
  }

  const planetSignMap = buildPlanetSignMap(chartData);
  const planetLongitudeMap = buildPlanetLongitudeMap(chartData);
  const planetsBySign = getPlanetsBySign(planetSignMap);
  const moonWaxing = getMoonWaxing(chartData);

  const charaKarakas = calculateCharaKarakas(chartData, useRahu);

  // Karakamsha: D9 sign of AK planet
  const akEntry = charaKarakas.find((k) => k.karaka === 'AK');
  let karakamshaSign: number | null = null;
  if (akEntry) {
    const akLongitude = chartData?.planets?.[akEntry.planet]?.longitude;
    if (typeof akLongitude === 'number') {
      karakamshaSign = getNavamsaSign(akLongitude);
    }
  }

  // Upapada Lagna: Arudha of 12th house
  const twelfthHouseSign = (lagnaSign + 11) % 12;
  const twelfthLordSign = getLordSign(twelfthHouseSign, planetSignMap);
  const upapadaLagna = twelfthLordSign !== null ? getArudhaPada(twelfthHouseSign, twelfthLordSign) : null;

  const mahadashaSequence = getSignSequence(lagnaSign, getDirection(lagnaSign));
  const charaDasha: JaiminiSnapshot['charaDasha'] = [];
  let currentCharaAntardasha: JaiminiSnapshot['currentCharaAntardasha'] = null;
  let currentCharaPratyantardasha: JaiminiSnapshot['currentCharaPratyantardasha'] = null;
  let cursor = new Date(birthInstant);

  mahadashaSequence.forEach((mdSign) => {
    const years = getDashaLength(mdSign, planetSignMap, planetLongitudeMap);
    const end = new Date(cursor.getTime() + (years * YEAR_MS));

    const antardashaSequence = getSubPeriodSequence(mdSign);
    const antardasha: Array<{
      sign: number;
      signName: string;
      years: number;
      start: Date;
      end: Date;
      pratyantardasha: Array<{ sign: number; signName: string; years: number; start: Date; end: Date }>;
    }> = [];
    let antarCursor = new Date(cursor);

    antardashaSequence.forEach((adSign) => {
      const adYears = years / 12;
      const adEnd = new Date(antarCursor.getTime() + (adYears * YEAR_MS));
      const pratyantardashaSequence = getSubPeriodSequence(adSign);
      const pratyantardasha: Array<{ sign: number; signName: string; years: number; start: Date; end: Date }> = [];
      let pratyantarCursor = new Date(antarCursor);

      pratyantardashaSequence.forEach((pdSign) => {
        const pdYears = adYears / 12;
        const pdEnd = new Date(pratyantarCursor.getTime() + (pdYears * YEAR_MS));

        const pratyantarEntry = {
          sign: pdSign,
          signName: SIGN_NAMES[pdSign],
          years: pdYears,
          start: new Date(pratyantarCursor),
          end: pdEnd
        };

        if (asOfDate >= pratyantarEntry.start && asOfDate < pratyantarEntry.end) {
          currentCharaPratyantardasha = pratyantarEntry;
        }

        pratyantardasha.push(pratyantarEntry);
        pratyantarCursor = pdEnd;
      });

      const antardashaEntry = {
        sign: adSign,
        signName: SIGN_NAMES[adSign],
        years: adYears,
        start: new Date(antarCursor),
        end: adEnd,
        pratyantardasha
      };

      if (asOfDate >= antardashaEntry.start && asOfDate < antardashaEntry.end) {
        currentCharaAntardasha = antardashaEntry;
      }

      antardasha.push(antardashaEntry);

      antarCursor = adEnd;
    });

    charaDasha.push({
      sign: mdSign,
      signName: SIGN_NAMES[mdSign],
      years,
      start: new Date(cursor),
      end,
      antardasha
    });

    cursor = end;
  });

  const currentCharaDasha = charaDasha.find((entry) => asOfDate >= entry.start && asOfDate < entry.end) || null;

  const arudhaProjection = calculateArudhaFromChartData(chartData, lagnaSign, planetSignMap, charaKarakas);

  const navamsaByPlanet: Record<string, { sign: number; signName: string }> = {};
  Object.entries(chartData?.planets || {}).forEach(([key, value]) => {
    const longitude = (value as any)?.longitude;
    if (typeof longitude !== 'number') return;

    const sign = getNavamsaSign(longitude);
    navamsaByPlanet[String(key).toUpperCase()] = {
      sign,
      signName: SIGN_NAMES[sign]
    };
  });

  const argalaBySign: JaiminiSnapshot['argalaBySign'] = {
    0: evaluateArgalaForSign(0, planetsBySign, moonWaxing),
    1: evaluateArgalaForSign(1, planetsBySign, moonWaxing),
    2: evaluateArgalaForSign(2, planetsBySign, moonWaxing),
    3: evaluateArgalaForSign(3, planetsBySign, moonWaxing),
    4: evaluateArgalaForSign(4, planetsBySign, moonWaxing),
    5: evaluateArgalaForSign(5, planetsBySign, moonWaxing),
    6: evaluateArgalaForSign(6, planetsBySign, moonWaxing),
    7: evaluateArgalaForSign(7, planetsBySign, moonWaxing),
    8: evaluateArgalaForSign(8, planetsBySign, moonWaxing),
    9: evaluateArgalaForSign(9, planetsBySign, moonWaxing),
    10: evaluateArgalaForSign(10, planetsBySign, moonWaxing),
    11: evaluateArgalaForSign(11, planetsBySign, moonWaxing)
  };

  return {
    lagnaSign,
    lagnaName: SIGN_NAMES[lagnaSign],
    karakamshaSign,
    karakamshaName: karakamshaSign !== null ? SIGN_NAMES[karakamshaSign] : null,
    upapadaLagna,
    upapadaLagnaName: upapadaLagna !== null ? SIGN_NAMES[upapadaLagna] : null,
    charaKarakas,
    currentCharaDasha,
    currentCharaAntardasha,
    currentCharaPratyantardasha,
    charaDasha,
    arudhaPadas: {
      AL: arudhaProjection.AL,
      houses: arudhaProjection.houses
    },
    arudha: arudhaProjection,
    lagnaDrishti: getRashiDrishti(lagnaSign),
    navamsaByPlanet,
    argalaBySign
  };
};

export const calculateJaiminiPredictions = (
  chartData: any,
  snapshot: JaiminiSnapshot,
  asOfDate: Date = new Date()
): JaiminiPredictionReport | null => {
  if (!chartData || !snapshot) return null;

  const moonWaxing = getMoonWaxing(chartData);
  const planetSignMap = buildPlanetSignMap(chartData);
  const bySign = getPlanetsBySign(planetSignMap);
  const karakaPlanetMap = Object.fromEntries(snapshot.charaKarakas.map((k) => [k.karaka, k.planet])) as Record<string, string>;

  const evaluateKaraka = (karakaCode: string): KarakaScoreResult => {
    const planet = karakaPlanetMap[karakaCode];
    const sign = planetSignMap[planet];
    const house = Number(chartData?.planets?.[planet]?.house) || null;
    const argala = sign !== undefined ? snapshot.argalaBySign[sign] : null;
    const details: string[] = [];
    let score = 0;

    if (house && [1, 4, 7, 10].includes(house)) {
      score += 2;
      details.push('Kendra placement +2');
    }
    if (house && [1, 5, 9].includes(house)) {
      score += 2;
      details.push('Trikona placement +2');
    }
    if (house && [6, 8, 12].includes(house)) {
      score -= 2;
      details.push('Dusthana placement -2');
    }

    const conjuncts = sign !== undefined ? bySign[sign].filter((p) => p !== planet) : [];
    conjuncts.forEach((p) => {
      if (isBeneficPlanet(p, moonWaxing)) {
        score += 1;
        details.push(`${p} conjunction +1`);
      } else {
        score -= 1;
        details.push(`${p} conjunction -1`);
      }
    });

    if (argala?.second.strength === 'strong') {
      score += 2;
      details.push('2nd Argala support +2');
    } else if (argala?.second.strength === 'obstructed') {
      score -= 2;
      details.push('2nd Argala obstruction -2');
    }

    if (karakaCode === 'AK') {
      if (house && [1, 5, 9].includes(house)) {
        score += 2;
        details.push('AK in Dharma house +2');
      }
    }

    if (karakaCode === 'AmK') {
      const akPlanet = karakaPlanetMap.AK;
      if (akPlanet && planetSignMap[akPlanet] === sign) {
        score += 3;
        details.push('AmK connected with AK +3');
      }
      if (house === 10) {
        score += 2;
        details.push('AmK in 10th house +2');
      }
    }

    if (karakaCode === 'DK') {
      if (house === 7) {
        score += 2;
        details.push('DK in 7th house +2');
      }
      const secondBenefics = argala?.second.argalaNature.benefics.length || 0;
      if (secondBenefics > 0) {
        score += 2;
        details.push('Benefic Argala on DK sign +2');
      }
    }

    return {
      karaka: karakaCode,
      planet,
      score,
      interpretation: interpretScore(score),
      details
    };
  };

  const karakas: Record<string, KarakaScoreResult> = {
    AK: evaluateKaraka('AK'),
    AmK: evaluateKaraka('AmK'),
    BK: evaluateKaraka('BK'),
    MK: evaluateKaraka('MK'),
    PK: evaluateKaraka('PK'),
    GK: evaluateKaraka('GK'),
    DK: evaluateKaraka('DK')
  };

  const AL = snapshot.arudhaPadas.AL;
  const A2 = snapshot.arudhaPadas.houses[2];
  const A7 = snapshot.arudhaPadas.houses[7];

  let wealthScore = 0;
  const wealthDetails: string[] = [];
  const a2Planets = bySign[A2] || [];
  const a2Argala = snapshot.argalaBySign[A2];

  a2Planets.forEach((planet) => {
    if (isBeneficPlanet(planet, moonWaxing)) {
      wealthScore += 2;
      wealthDetails.push(`${planet} in A2 +2`);
    } else {
      wealthScore -= 1;
      wealthDetails.push(`${planet} in A2 -1`);
    }
  });

  if (a2Argala.second.strength === 'strong') {
    wealthScore += 2;
    wealthDetails.push('A2 Argala support +2');
  } else if (a2Argala.second.strength === 'obstructed') {
    wealthScore -= 2;
    wealthDetails.push('A2 Argala obstruction -2');
  }

  if (A2 === AL) {
    wealthScore += 2;
    wealthDetails.push('A2 linked with AL +2');
  }

  const dkPlanet = karakaPlanetMap.DK;
  const dkSign = planetSignMap[dkPlanet];
  let marriageScore = 0;
  const marriageDetails: string[] = [];
  const a7Planets = bySign[A7] || [];
  const a7Argala = snapshot.argalaBySign[A7];

  a7Planets.forEach((planet) => {
    if (isBeneficPlanet(planet, moonWaxing)) {
      marriageScore += 2;
      marriageDetails.push(`${planet} in A7 +2`);
    } else {
      marriageScore -= 1;
      marriageDetails.push(`${planet} in A7 -1`);
    }
  });

  if (dkSign === A7) {
    marriageScore += 3;
    marriageDetails.push('DK connected with A7 +3');
  }

  if (a7Argala.second.strength === 'strong') {
    marriageScore += 2;
    marriageDetails.push('A7 Argala support +2');
  } else if (a7Argala.second.strength === 'obstructed') {
    marriageScore -= 2;
    marriageDetails.push('A7 Argala obstruction -2');
  }

  const marriageActivePeriods = snapshot.charaDasha
    .filter((md) => md.end > asOfDate)
    .filter((md) => md.sign === dkSign || md.sign === A7 || getRashiDrishti(md.sign).includes(A7))
    .map((md) => ({ sign: md.sign, signName: md.signName, start: md.start, end: md.end }));

  const wealthActivePeriods = snapshot.charaDasha
    .filter((md) => md.end > asOfDate)
    .filter((md) => md.sign === A2 || md.sign === AL || getRashiDrishti(md.sign).includes(A2))
    .map((md) => ({ sign: md.sign, signName: md.signName, start: md.start, end: md.end }));

  return {
    karakas,
    wealth: {
      score: wealthScore,
      prediction: interpretWealth(wealthScore),
      details: wealthDetails,
      activePeriods: wealthActivePeriods
    },
    marriage: {
      score: marriageScore,
      prediction: interpretMarriage(marriageScore),
      details: marriageDetails,
      activePeriods: marriageActivePeriods
    }
  };
};
